import React, { useState, useEffect } from 'react';
import { Camera, X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Wand2, Plus, ArrowRight, ArrowLeft, Lock } from 'lucide-react';
import { CreativeBrief, CampaignConcept, CampaignImage, AppStep, GenderPreference, IdentityAnchors } from './types';
import { generateCampaignConcepts, generateTwinImage, refineBriefPrompt } from './services/geminiService';

declare global {
  var aistudio: {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  } | undefined;
}

const REALISM_PROFILES = [
  "Baddie Editorial", "High-Glam Commercial", "Shot on iPhone (Pro)", "Raw Cinema", "Analog Film", "Hyper-Realistic Studio", "Night Vision / Flash", "Magazine Cover Style"
];

const LIGHTING_OPTIONS = [
  "Studio Glam (Bright, Even)", "Neon Glow (Cyberpunk)", "Hard Flash (Paparazzi Style)", "Golden Hour (Sun-kissed)", "Sunset Rooftop (Warm, Glowy)", "Butterfly (Dramatic Beauty)", "Ring Light (Content Creator)", "Underlit (Moody / Mysterious)", "Natural Window Light", "Blue Hour (Cinematic)", "Studio Softbox Lighting"
];

const LENS_OPTIONS = [
  "35mm (Natural, Classic)", "85mm (Creamy Portrait Bokeh)", "24mm (Dramatic Wide)", "Wide Angle (Tall & Leggy look)", "Anamorphic (Cinematic Baddie)", "Fisheye (Experimental / Y2K)", "Macro (Extreme Detail)"
];

const BODY_TYPE_OPTIONS = ["As in photo", "Snatched", "Athletic Sculpt", "Hourglass", "Model Lean", "Muscular / Built", "Statuesque / Tall", "Petite", "Natural / Average"];

const BADDIE_SCENES = [
  "Dubai Skyline Balcony", "Manhattan Penthouse", "London Mayfair Apartment", "LA Hills View", "Paris Rooftop", "Tokyo Night Neon", "Monaco Marina", "Maldives Water Villa", "Tulum Beach Club", "Ibiza Sunset Terrace", "Santorini Cliffside", "Miami South Beach", "Bali Infinity Pool", "Luxury Hotel Suite", "Marble Bathroom", "Modern Glass Mansion", "Walk-In Wardrobe", "Glam Dressing Room", "Velvet Sofa Lounge", "Minimalist White Kitchen", "High Fashion Studio", "Vogue Cover Backdrop", "Soft Pink Studio", "Black Seamless Backdrop", "Spotlight Stage", "Moody Studio Shadows", "Neon Light Tunnel", "Dubai Penthouse Energy", "Monaco Yacht Club", "Designer Shopping Spree", "Champagne Balcony at Sunset", "Black Tie Gala", "VIP Rooftop Party", "After-Hours Club", "Neon Alleyway", "Leather & Chrome Interior", "Noir Film Still", "Underground Art Gallery", "Minimalist Penthouse", "Silk Robe Morning", "Skincare Commercial Glow", "Parisian Balcony", "Beige Aesthetic Café", "Golden Hour Window Light", "Miami Poolside", "Ibiza Beach Club", "Coachella Energy", "Luxury Gym Mirror Selfie", "Selfie Flash Night Out", "Airport Fit Check", "Boardroom After Hours", "Fashion Week Backstage", "Luxury Car Interior", "Inside a Luxury G-Wagon", "Designer Boutique Fitting Room", "Penthouse Overlooking NYC", "High-End Sushi Restaurant", "VIP Lounge at Nightclub", "Yacht Deck at Sunset", "Aesthetic Pink Bedroom", "Private Jet Cabin", "Minimalist Art Gallery", "Rooftop Helipad", "Tennis Court Chic", "Modern Gym Neon", "Walk-in Closet", "Lobby of 5-Star Hotel", "Cyber-Punk Arcade", "Parisian Cafe Sidewalk", "Sun-drenched Poolside", "First Class Lounge", "Rich Mom Kitchen", "Desert Luxury Resort", "Concrete Bunker", "Tech Baddie Office", "High-Rise Infinity Pool", "Rodeo Drive Shopping", "Luxury Car Dealership", "Studio Lighting", "Brutalist Stairs", "Aesthetic Juice Bar", "Sunset Beach Walk", "Luxury Spa Oasis", "Ski Resort Lounge", "Modern Library", "Industrial Loft", "Lakeside Villa", "Urban Rooftop", "Floral Fantasy Garden", "Concrete Courtyard", "Grand Marble Staircase", "Scandi Chic Living", "Podcast Studio", "Art Private Vault", "Steampunk Loft", "Futuristic Greenhouse", "Holographic Space", "Abandoned Warehouse", "Tokyo Night Street", "Venetian Canal", "Santorini Balcony", "Modern Padel Court", "Designer Showroom", "Enchanted Forest", "Skyscraper Gym", "High-Tech Lab"
];

const FEMALE_OPTIONS = {
  vibe: ["Old Money Luxe", "Private Jet Princess", "Mafia Wife", "Clean Girl Luxury", "Instagram Influencer", "Street Style Paparazzi", "CEO Office Portrait", "Podcast Host Glow", "Creative Director", "Magazine Cover Shoot", "Tech Baddie", "Mafia Baddie", "Rich Girl Energy", "Y2K Baddie", "Quiet Luxury", "Clean Baddie", "Cozy Baddie", "Dark Feminine", "Cyber Baddie", "Old Money Baddie", "Streetwear Hero", "Glowy Minimalist", "High-Voltage Neon", "Barbiecore Baddie", "Nordic Noir", "Luxury Nomad", "Avant-Garde", "Siren Aesthetic"],
  scene: BADDIE_SCENES,
  outfit: ["As in photo", "Figure-hugging black dress", "Cut-out bodycon dress", "Satin slip dress", "Corset top + tailored trousers", "Sheer mesh dress", "Structured blazer dress", "Metallic mini dress", "Backless evening gown", "Leather mini skirt + boots", "All-black fitted outfit", "Latex bodysuit", "High-slit maxi dress", "Lace corset + tailored pants", "Black oversized blazer + heels", "Sheer tights + platform heels", "Dark monochrome street look", "Silk co-ord set", "Cream knit dress", "Cashmere lounge set", "Oversized white shirt + heels", "Neutral-toned fitted dress", "Wide-leg trousers + bralette", "Satin robe set", "Minimalist beige blazer set", "Micro bikini + sarong", "Cut-out swimsuit", "Linen co-ord set", "Low-rise trousers + crop top", "Beach club dress", "Flowing maxi dress", "Oversized sunglasses + swimwear", "Ibiza sunset party outfit", "Tailored power suit", "Fitted blazer + stilettos", "Sharp pencil skirt + blouse", "Statement coat + boots", "Luxury trench coat", "Designer athleisure", "Silk blouse + tailored trousers", "CEO monochrome set", "Designer Tracksuit Set", "Fur Coat & Mini Skirt", "Corset & Cargoes", "Tweed Set", "Vinyl Jumpsuit", "Chrome Armor Corset", "Distressed Denim Couture"],
  hair: ["As in photo", "Sleek Straight", "Big Glam Volume", "Soft Waves", "Wet Look", "Slick Back Bun", "High Ponytail", "Sleek Blowout", "Half-Up Half-Down", "Long Textured"],
  makeup: ["As in photo", "Soft Glam", "Full Beat", "Bronzed Goddess", "Glossy Nude", "Smokey Night", "The Baddie Beat", "Dramatic Cat Eye", "Matte Snatched", "Dewy / Radiant", "Red Lip Editorial"],
  anatomicalLabels: { makeup: "Makeup Era", bust: "Upper Frame", waist: "Waist Energy", hips: "Curve Balance" },
  anatomicalValues: { bust: ["As in photo", "Natural", "Sculpted", "Enhanced"], waist: ["As in photo", "Snatched", "Slim", "Natural"], hips: ["As in photo", "Hourglass", "Curvy", "Athletic"] }
};

const MALE_OPTIONS = {
  vibe: ["Old Money Elite", "Dubai Royal Energy", "Hypebeast Executive", "Stock Market Whale", "Modern Day Gatsby", "Digital Nomad Elite", "Stealth Wealth CEO", "Crypto King Aesthetic", "Startup Hustler", "Billionaire Clubhouse", "Street Style Hero", "Minimalist Street-Couture", "Podcast Host", "Creative Director", "Executive Baddie", "Quiet Wealth", "Modern Rockstar", "Aesthetic Gym Rat", "High-Stakes Gambler", "Jetset Entrepreneur", "Streetwear Savant", "Clean Cut Mogul", "Dark Knight Energy", "Midnight Drifter", "Phonk Aesthetic", "Designer Techwear", "Varsity Baddie", "First Class Nomad", "Rugged / Street", "Fitness Hero", "Minimalist Power", "Cyber Drifter", "Nordic Noir", "Modern Samurai", "Luxury Nomad", "Dark Academic", "Sartorial Rebel", "Urban Alchemist", "Stealth Ops Executive"],
  scene: BADDIE_SCENES,
  outfit: ["As in photo", "All-black fitted outfit", "Leather jacket + fitted tee", "Slim black suit, no tie", "Oversized dark coat", "Black turtleneck + trousers", "Chelsea boots + slim trousers", "Street-lux tracksuit", "Dark layered streetwear", "Tailored navy suit", "Open-collar power suit", "Double-breasted suit", "Luxury overcoat", "Designer loafers + slim fit trousers", "Smart-casual blazer + fitted tee", "High-end minimalist fit", "Watch-focused power look", "Linen shirt half unbuttoned", "Fitted vest + tailored shorts", "Resort co-ord set", "Designer sunglasses + summer fit", "White trousers + loafers", "Beach club relaxed fit", "Gold chain + open shirt", "Poolside relaxed luxury", "Clean oversized hoodie", "Luxury bomber jacket", "High-end streetwear fit", "Fitted tank + trousers", "Statement sneakers + relaxed fit", "Minimalist all-white outfit", "Dark denim + designer jacket", "Studio-ready casual fit", "Power Suit", "Tailored Three-Piece", "Cashmere & Silk Chinos", "Tuxedo (Modern)", "Workout Compressions"],
  hair: ["As in photo", "Sleek Straight", "Big Glam Volume", "Soft Waves", "Wet Look", "Slick Back Bun", "Sleek Fade", "Slick Back", "Classic Part", "Long Textured", "Buzz Cut"],
  makeup: ["As in photo", "Clean Shaven", "Heavy Stubble", "Full Groomed Beard", "Designer Stubble"],
  anatomicalLabels: { makeup: "Grooming Era", bust: "Upper Frame", waist: "Waist Energy", hips: "Curve Balance" },
  anatomicalValues: { bust: ["As in photo", "Broad / Heroic", "Natural", "Slim Tailored"], waist: ["As in photo", "Lean V-Taper", "Natural", "Athletic"], hips: ["As in photo", "Hourglass", "Curvy", "Athletic"] }
};

const OPTIONS_MAP = { female: FEMALE_OPTIONS, male: MALE_OPTIONS };

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [activeTab, setActiveTab] = useState<StudioTab>('identity');
  // hasKey starts as false to force the authentication flow
  const [hasKey, setHasKey] = useState<boolean>(false);
  const [isAuthChecking, setIsAuthChecking] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');

  const [brief, setBrief] = useState<CreativeBrief>({
    gender: 'female', vibe: FEMALE_OPTIONS.vibe[0], scene: FEMALE_OPTIONS.scene[0], outfit: FEMALE_OPTIONS.outfit[0],
    hairStyle: FEMALE_OPTIONS.hair[0], makeupPalette: FEMALE_OPTIONS.makeup[0], skinStyle: "Natural Glow",
    realismTuning: "Baddie Editorial", bodyType: "As in photo", bustLine: "As in photo", waistRatio: "As in photo", hipsSculpt: "As in photo",
    cameraAngle: "Standard Eye Level", lighting: LIGHTING_OPTIONS[0], lens: LENS_OPTIONS[0],
    aspectRatio: "9:16", numImages: 4, customPrompt: "", anchorImages: { frontal: null, left: null, right: null }, imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    const checkAuth = async () => {
      // 1. Check AI Studio Bridge
      if (typeof window.aistudio !== 'undefined' && window.aistudio.hasSelectedApiKey) {
        try {
          const selected = await window.aistudio.hasSelectedApiKey();
          if (selected) {
            setHasKey(true);
            setIsAuthChecking(false);
            return;
          }
        } catch (e) { console.warn("Bridge check failed", e); }
      } 
      
      // 2. Check Local Storage (for deployed/BYOK scenarios)
      const localKey = localStorage.getItem('gemini_api_key');
      if (localKey) {
        setHasKey(true);
      } else {
        setHasKey(false);
      }
      
      setIsAuthChecking(false);
    };
    checkAuth();
  }, []);

  const handleStudioAuth = async () => {
    if (typeof window.aistudio !== 'undefined' && window.aistudio.openSelectKey) {
      await window.aistudio.openSelectKey();
      setHasKey(true);
    }
  };

  const handleManualKeySubmit = () => {
    if (apiKeyInput.trim().length > 10) {
      localStorage.setItem('gemini_api_key', apiKeyInput.trim());
      setHasKey(true);
    } else {
      alert("Please enter a valid API key.");
    }
  };

  const handleResetKey = () => {
    localStorage.removeItem('gemini_api_key');
    setHasKey(false);
    setApiKeyInput('');
    setStep('landing');
  };

  const initiateProduction = async () => {
    if (!brief.anchorImages.frontal) return alert("Please upload at least a Frontal Identity Anchor.");
    setIsGenerating(true);
    try {
      const generated = await generateCampaignConcepts(brief);
      const initialImages: CampaignImage[] = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, themeIndex: generated[0].themeIndex, status: 'idle'
      }));
      setImages(initialImages);
      
      for (const img of initialImages) {
        setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'generating' } : p));
        try {
          const url = await generateTwinImage(brief, generated[0].prompt);
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'completed', imageUrl: url } : p));
        } catch (e: any) {
          if (e.message === "KEY_RESET_REQUIRED") {
            setHasKey(false);
            localStorage.removeItem('gemini_api_key');
            break;
          }
          setImages(prev => prev.map(p => p.id === img.id ? { ...p, status: 'failed' } : p));
        }
      }
    } catch (e: any) {
      if (e.message === "KEY_RESET_REQUIRED") {
        setHasKey(false);
        localStorage.removeItem('gemini_api_key');
      }
      else alert("Production error. Check connection.");
    } finally {
      setIsGenerating(false);
    }
  };

  if (isAuthChecking) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#dffe00] animate-spin" />
    </div>
  );

  // Unified Menu/Auth Screen
  if (!hasKey) {
    const isStudioEnv = typeof window.aistudio !== 'undefined';
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-6 relative">
        <div className="max-w-md w-full flex flex-col items-center text-center animate-fade-in space-y-8 z-10">
          
          <div className="p-4 rounded-full border border-white/10 bg-white/5 mb-4">
            <Lock className="w-6 h-6 text-white/50" />
          </div>

          <h1 className="text-3xl md:text-5xl font-serif text-white tracking-wide headline-style">
            AI Twin Studio: <span className="text-[#dffe00]">Baddie</span>
          </h1>

          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40">
            Connect Studio Access Key To Begin
          </p>

          {isStudioEnv ? (
            // Studio Bridge Environment Button
            <button 
              onClick={handleStudioAuth} 
              className="w-full max-w-sm py-4 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-full hover:bg-[#dffe00] transition-all shadow-xl"
            >
              Connect Project Key
            </button>
          ) : (
            // Deployed / Manual Input Environment
            <div className="flex flex-col gap-4 w-full max-w-sm">
              <input 
                type="password"
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                placeholder="Paste Gemini API Key"
                className="bg-transparent border border-white/20 rounded-lg px-4 py-3 text-center text-white placeholder:text-white/30 focus:outline-none focus:border-[#dffe00] text-[12px] tracking-wider transition-all"
              />
              <button 
                onClick={handleManualKeySubmit} 
                className="w-full py-4 bg-white text-black text-[11px] font-bold uppercase tracking-[0.2em] rounded-full hover:bg-[#dffe00] transition-all shadow-xl"
              >
                Activate Studio
              </button>
            </div>
          )}

          <div className="w-12 h-[1px] bg-white/10 mt-8" />

          <a 
            href="https://aistudio.google.com/app/api-keys" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-[9px] uppercase tracking-widest text-white/30 hover:text-white transition-colors flex items-center gap-2"
          >
            Get your free API key from Google <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    );
  }

  // Authenticated Landing Page
  if (step === 'landing') {
    return (
      <div className="relative min-h-screen bg-[#050505] flex flex-col items-center justify-center p-8">
        <div className="relative z-10 text-center animate-fade-in max-w-2xl">
          <h1 className="text-4xl md:text-6xl font-serif text-white mb-4 headline-style tracking-[2px]">AI Twin Studio: <span className="text-[#dffe00]">Baddie</span></h1>
          <p className="text-white/40 uppercase tracking-[0.4em] text-[9px] mb-12">Digital Persona Fabrication</p>
          <button onClick={() => setStep('setup')} className="px-10 py-5 border border-white/10 rounded-full text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white hover:text-black transition-all">Enter Studio</button>
        </div>
        
        <div className="absolute bottom-8 text-center w-full z-10">
            <button onClick={handleResetKey} className="text-[9px] uppercase tracking-widest text-white/20 hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto">
              <Key className="w-3 h-3" /> Update API Key
            </button>
        </div>
      </div>
    );
  }

  const tabs: {id: StudioTab, label: string}[] = [
    {id: 'identity', label: 'Identity'},
    {id: 'creative', label: 'Baddie Brief'},
    {id: 'likeness', label: 'Signature Sculpt'},
    {id: 'optics', label: 'Final Polish'}
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#dffe00] selection:text-black pb-32">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-5 flex justify-between items-center bg-gradient-to-b from-[#050505] to-transparent backdrop-blur-sm border-b border-white/5">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => setStep('landing')}>
          <span className="font-serif text-[11px] tracking-widest headline-style text-white">AI Twin Studio: <span className="text-[#dffe00]">Baddie</span></span>
        </div>
        <button className="text-[8px] font-bold uppercase tracking-widest text-white/40 hover:text-white transition-colors" onClick={() => {
           setStep('landing');
        }}>Exit Studio</button>
      </nav>

      <main className="max-w-4xl mx-auto px-6 pt-32">
        <div className="flex justify-center mb-12 overflow-x-auto scrollbar-hide">
          <div className="flex bg-white/[0.03] p-1 rounded-full border border-white/10">
            {tabs.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-5 py-2 rounded-full text-[9px] font-bold uppercase tracking-widest whitespace-nowrap transition-all duration-300 ${activeTab === tab.id ? 'bg-[#dffe00] text-black shadow-lg' : 'text-white/40 hover:text-white'}`}>{tab.label}</button>
            ))}
          </div>
        </div>

        <div className="min-h-[350px]">
          {activeTab === 'identity' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="01" title="Identity Anchors" />
              <div className="mb-10 flex bg-white/[0.03] p-1 rounded-full border border-white/10">
                {['female', 'male'].map((g) => (
                  <button key={g} onClick={() => {
                    const nextOptions = OPTIONS_MAP[g as GenderPreference];
                    setBrief(p => ({ ...p, gender: g as GenderPreference, vibe: nextOptions.vibe[0], scene: nextOptions.scene[0], outfit: nextOptions.outfit[0], hairStyle: nextOptions.hair[0], makeupPalette: nextOptions.makeup[0] }));
                  }} className={`px-6 py-2.5 rounded-full text-[9px] font-bold uppercase tracking-widest transition-all ${brief.gender === g ? 'bg-[#dffe00] text-black' : 'text-white/40 hover:text-white'}`}>{g === 'female' ? 'Her' : 'Him'}</button>
                ))}
              </div>
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-5">
                <AnchorSlot label="Frontal (Face Ref)" value={brief.anchorImages.frontal} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, frontal: val } }))} />
                <AnchorSlot label="Left Angle" value={brief.anchorImages.left} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, left: val } }))} />
                <AnchorSlot label="Right Angle" value={brief.anchorImages.right} onUpload={val => setBrief(p => ({ ...p, anchorImages: { ...p.anchorImages, right: val } }))} />
              </div>
              <div className="mt-12 w-full flex justify-end">
                <button onClick={() => setActiveTab('creative')} className="flex items-center gap-2 px-6 py-3.5 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all">Next: Baddie Brief <ArrowRight className="w-3.5 h-3.5" /></button>
              </div>
            </section>
          )}

          {activeTab === 'creative' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="02" title="Creative Direction" />
              <div className="w-full grid grid-cols-1 md:grid-cols-3 gap-6">
                <StudioDropdown label="AESTHETIC VIBE" value={brief.vibe} options={OPTIONS_MAP[brief.gender].vibe} onChange={v => setBrief(p => ({ ...p, vibe: v }))} />
                <StudioDropdown label="LOCATION" value={brief.scene} options={OPTIONS_MAP[brief.gender].scene} onChange={v => setBrief(p => ({ ...p, scene: v }))} />
                <StudioDropdown label="CURATED ATTIRE" value={brief.outfit} options={OPTIONS_MAP[brief.gender].outfit} onChange={v => setBrief(p => ({ ...p, outfit: v }))} />
              </div>
              <div className="mt-20 w-full flex justify-between">
                <button onClick={() => setActiveTab('identity')} className="flex items-center gap-2 px-6 py-3 text-white/40 hover:text-white text-[9px] font-bold uppercase tracking-widest transition-all"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
                <button onClick={() => setActiveTab('likeness')} className="flex items-center gap-2 px-6 py-3.5 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all">Next: Signature Sculpt <ArrowRight className="w-3.5 h-3.5" /></button>
              </div>
            </section>
          )}

          {activeTab === 'likeness' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="03" title="Signature Sculpt" />
              <div className="w-full grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-8">
                <StudioDropdown label="Hair Energy" value={brief.hairStyle} options={OPTIONS_MAP[brief.gender].hair} onChange={v => setBrief(p => ({ ...p, hairStyle: v }))} />
                <StudioDropdown label={OPTIONS_MAP[brief.gender].anatomicalLabels.makeup} value={brief.makeupPalette} options={OPTIONS_MAP[brief.gender].makeup} onChange={v => setBrief(p => ({ ...p, makeupPalette: v }))} />
                <StudioDropdown label="Glow Level" value={brief.skinStyle} options={["Natural Glow", "Glass Skin", "Golden Hour", "Matte Luxe"]} onChange={v => setBrief(p => ({ ...p, skinStyle: v }))} />
                <StudioDropdown label="Realism Profile" value={brief.realismTuning} options={REALISM_PROFILES} onChange={v => setBrief(p => ({ ...p, realismTuning: v }))} />
                <StudioDropdown label="Silhouette" value={brief.bodyType} options={BODY_TYPE_OPTIONS} onChange={v => setBrief(p => ({ ...p, bodyType: v }))} />
              </div>
              <div className="mt-20 w-full flex justify-between">
                <button onClick={() => setActiveTab('creative')} className="flex items-center gap-2 px-6 py-3 text-white/40 hover:text-white text-[9px] font-bold uppercase tracking-widest transition-all"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
                <button onClick={() => setActiveTab('optics')} className="flex items-center gap-2 px-6 py-3.5 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-white hover:text-black transition-all">Next: Final Polish <ArrowRight className="w-3.5 h-3.5" /></button>
              </div>
            </section>
          )}

          {activeTab === 'optics' && (
            <section className="flex flex-col items-center animate-fade-in">
              <PhaseHeader phase="04" title="Final Production" />
              <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <StudioDropdown label="Lighting Rig" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({ ...p, lighting: v }))} />
                <StudioDropdown label="Optics" value={brief.lens} options={LENS_OPTIONS} onChange={v => setBrief(p => ({ ...p, lens: v }))} />
                <StudioDropdown label="Angle" value={brief.cameraAngle} options={["Eye Level", "Low Angle Hero", "High Angle Soft"]} onChange={v => setBrief(p => ({ ...p, cameraAngle: v }))} />
                <StudioDropdown label="Format" value={brief.aspectRatio} options={[{value:"9:16", label:"Vertical (9:16)"}, {value:"1:1", label:"Square (1:1)"}, {value:"16:9", label:"Widescreen (16:9)"}]} onChange={v => setBrief(p => ({ ...p, aspectRatio: v as any }))} />
              </div>
              <button onClick={initiateProduction} disabled={isGenerating} className="px-16 py-6 bg-[#dffe00] text-black text-[11px] font-bold uppercase tracking-[0.4em] rounded-full hover:bg-white transition-all shadow-2xl disabled:opacity-30">{isGenerating ? "Synthesizing..." : "Initiate Production"}</button>
            </section>
          )}
        </div>

        {images.length > 0 && (
          <section id="production-results" className="pt-24 animate-fade-in border-t border-white/5 mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {images.map(img => (
              <div key={img.id} className="aspect-[3/4] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group relative flex items-center justify-center">
                {img.status === 'completed' ? (
                  <>
                    <img src={img.imageUrl} className="w-full h-full object-cover" alt="Master" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-3 backdrop-blur-md">
                      <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-3 bg-white text-black rounded-full"><Maximize2 className="w-4 h-4" /></button>
                      <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'baddie.png'; link.click(); }} className="p-3 bg-[#dffe00] text-black rounded-full"><Download className="w-4 h-4" /></button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    {img.status === 'failed' ? <AlertCircle className="w-5 h-5 text-red-500/50" /> : <Loader2 className="w-6 h-6 text-[#dffe00]/20 animate-spin" />}
                    <span className="text-[7px] uppercase tracking-widest text-white/20">{img.status === 'generating' ? 'Synthesizing' : 'Queued'}</span>
                  </div>
                )}
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="fixed bottom-6 right-8 z-40">
        <button 
          onClick={handleResetKey}
          className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-white/20 hover:text-[#dffe00] transition-colors bg-[#050505]/80 backdrop-blur-md px-4 py-2 rounded-full border border-white/5 hover:border-[#dffe00]/50 shadow-lg"
        >
          <Key className="w-3 h-3" />
          <span>Update API Key</span>
        </button>
      </footer>

      {previewImage && <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-8" onClick={() => setPreviewImage(null)}><img src={previewImage} className="max-w-full max-h-full object-contain" alt="Preview" /></div>}
    </div>
  );
};

const PhaseHeader: React.FC<{ phase: string; title: string }> = ({ phase, title }) => (
  <div className="flex flex-col items-center gap-2 mb-10 text-center animate-fade-in">
    <span className="text-[8px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase {phase}</span>
    <h2 className="text-xl md:text-2xl font-serif text-white tracking-tight headline-style">{title}</h2>
    <div className="w-6 h-[1px] bg-[#dffe00]/30 mt-3" />
  </div>
);

const StudioDropdown: React.FC<{ label: string; value: string; options: any[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[8px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-3.5 px-5 text-[11px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 transition-all cursor-pointer">
        {options.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lbl = typeof opt === 'string' ? opt : opt.label;
          return <option key={val} value={val} className="bg-[#050505]">{lbl}</option>;
        })}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const AnchorSlot: React.FC<{ label: string; value: string | null; onUpload: (data: string | null) => void }> = ({ label, value, onUpload }) => (
  <div className="flex flex-col gap-3 flex-1">
    <label className="text-[8px] font-bold uppercase tracking-widest text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[4/5] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer">
          <Plus className="w-6 h-6 stroke-[1px]" />
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (ev) => onUpload(ev.target?.result as string);
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

type StudioTab = 'identity' | 'creative' | 'likeness' | 'optics';

export default App;